const { debounce } = window.BAO.Utils

class CartItem extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['remove']
  }

  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.removeLineItem = this.removeLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.toggleEditDrawer = this.toggleEditDrawer.bind(this)
    this.updateLineItem = this.updateLineItem.bind(this)
    this.debouncedOnQuantityChange = debounce((event) => {
      this.onQuantityChange(event)
    }, 500)

    if (this.els?.remove.exists) {
      this.listeners.add(this.els.remove.element, 'click', this.removeLineItem)
    }

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )

    if (this.els?.['edit-trigger']?.exists) {
      this.els?.['edit-trigger']?.elements.forEach((el) => {
        this.listeners.add(el, 'click', this.toggleEditDrawer)
      })
    }

    if (this.els?.update?.exists) {
      this.listeners.add(this.els.update.element, 'click', this.updateLineItem)
    }
  }

  async updateLineItem () {
    const id = this.els.variant.element.value

    let quantity = 1
    const maxQuantity =
      this.els.variant.element.options[this.els.variant.element.selectedIndex]
        ?.dataset?.maxInventory ?? 1
    const currentQuantity = this.dataset.quantity ?? 1
    quantity = Math.min(currentQuantity, maxQuantity)

    if (!id) {
      console.error('Variant ID is missing or invalid')
      return
    }

    if (quantity <= 0) {
      console.error('Invalid quantity selected')
      return
    }

    this.loading = true

    try {
      // Add the new item and don't request sections
      const addState = await window.BAO.Cart.addItem(
        { id, quantity },
        { sections: [] },
      )

      const newItemExists = addState?.items?.some(
        (item) => item.id === parseInt(id),
      )

      if (!newItemExists) {
        console.error('New item was not added to the cart. Aborting removal.')
        return
      }

      // Remove the old item
      const removeState = await window.BAO.Cart.removeItem(this.dataset.key, {
        sections: this.mappedSections,
      })

      // Render sections
      if ('sections' in removeState) {
        this.renderSections(removeState.sections)
      }
    } catch (error) {
      console.error('Failed to update line item:', error)
    } finally {
      this.loading = false
    }
  }

  toggleEditDrawer () {
    const currentState =
      this.els['edit-drawer'].element.getAttribute('aria-hidden') === 'true'
    this.els['edit-drawer'].element.setAttribute('aria-hidden', !currentState)
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  async removeLineItem (e) {
    e.preventDefault()

    this.loading = true

    const state = await window.BAO.Cart.removeItem(this.dataset.key, {
      sections: this.mappedSections,
    })

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)
  }

  async onQuantityChange (e) {
    this.loading = true

    const state = await window.BAO.Cart.updateQuantity(
      this.dataset.key,
      e.detail.value,
      { sections: this.mappedSections },
    )

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)
  }

  get mappedSections () {
    return this.sections.map((s) => s.section)
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.inPushCart) {
      this.pushCart.loading = val
    }

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
